import React from 'react';

const PrivacyPage = () => {
  // It's good practice to include the last updated date.
  const lastUpdatedDate = "April 14, 2025"; // Update this whenever the policy changes

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">PokketCoach Privacy Policy</h1>
      <p className="mb-6 text-sm text-gray-600">Last Updated: {lastUpdatedDate}</p>

      {/* Introduction and Core Disclaimers */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Introduction & Important Notices</h2>
        <p className="mb-4">
          This Privacy Policy (the “Policy”) describes how PokketCoach, Inc. and its affiliates (“PokketCoach,” “we,” “our,” or “us”) collect, use, process, and safeguard your information when you use the PokketCoach mobile application, related websites, and other services that link to this Policy (collectively, the “Services”).
        </p>

        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
          <p className="font-bold mb-2">Important Notice Regarding Service Nature:</p>
          <p className="mb-2">
            PokketCoach provides tools, including psychometric tests, progress tracking, and interaction with an AI assistant named "Pokket," designed to help users explore sports psychology concepts. <strong>PokketCoach, including the Pokket AI, is an informational and educational tool powered by artificial intelligence. It is NOT a licensed or certified sports psychologist, clinical psychologist, therapist, counselor, or medical professional.</strong>
          </p>
          <p>
            <strong>The Services do NOT provide medical advice, diagnosis, or treatment, and your use does NOT create a therapist-client relationship.</strong> Relying on information from the Services is solely at your own risk. Always consult with a qualified healthcare provider or certified professional for any mental health concerns.
          </p>
        </div>

        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-6" role="alert">
          <p className="font-bold mb-2">Important Notice Regarding Data Use:</p>
          <p>
            <strong>No Sale of Personal Data: PokketCoach does not sell your personal information to third-party data brokers or advertising networks.</strong> Our business model focuses on providing valuable services, not monetizing your data through sales to others.
          </p>
        </div>

        <p className="mb-4">
          By accessing or using our Services, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Policy. If you do not agree, please do not use the Services.
        </p>
      </section>

      {/* Information We Collect */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
        <p className="mb-4">We collect information when you use our Services. The types depend on your usage. Some information is necessary to provide the Services; if you don't provide it or ask us to delete it, you may not be able to use certain features.</p>
        <p className="mb-4"><strong>Information you provide directly to us:</strong></p>
        <ul className="list-disc ml-6 mb-4 space-y-2">
          <li>Email address</li>
          <li>User name</li>
          <li>Payment information (if applicable, via our payment processors)</li>
          <li>Your interactions and communications with the Pokket AI coach</li>
          <li>Results and responses from psychometric tests or assessments within the Services</li>
          <li>Data you input for progress tracking (e.g., goals, journal entries, self-ratings)</li>
          <li>Contents of communications with us (e.g., support requests, feedback)</li>
        </ul>
        <p className="mb-4">
           You are not required to provide this, but some features depend on it. Some data, like test results and chat content, may be sensitive. <strong>Remember, this information is processed by our AI systems and is not reviewed or interpreted by certified mental health professionals in the context of providing therapy or diagnosis.</strong>
        </p>

        <p className="mb-4"><strong>Information we collect automatically:</strong></p>
        <p className="mb-4">
          We and certain third-party service providers (for analytics and functionality like cloud hosting) may use cookies and tracking technologies to collect technical information about your device and usage. This may include:
        </p>
        <ul className="list-disc ml-6 mb-4 space-y-2">
          <li>Browser type, ISP, operating system</li>
          <li>Domain name, access times, referring/exit pages, page views</li>
          <li>IP address, unique device identifiers (e.g., IDFA, Android ID)</li>
          <li>Service version, device type</li>
          <li>Usage patterns (frequency, navigation, feature interaction)</li>
          <li>General location inferred from IP address</li>
        </ul>
        <p className="mb-4">
          We use this information solely for internal purposes like analytics (understanding usage, improving performance) and ensuring service functionality. <strong>We do not use this automatically collected data for third-party targeted advertising.</strong> (See Section 8 on Cookies & Analytics).
        </p>

        <p className="mb-4"><strong>Information we collect from other sources:</strong></p>
        <ul className="list-disc ml-6 mb-4 space-y-2">
          <li>Referrals from other users.</li>
          <li>Interactions with our social media profiles (e.g., on Facebook, Google).</li>
          <li>Information from connected third-party accounts (like Google or Apple sign-in) if you choose to link them, based on your permissions with that third party.</li>
        </ul>
      </section>

      {/* How We Use Your Information */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
        <p className="mb-4">We use your information for the following purposes:</p>
        <ul className="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Provide & Improve Services:</strong> Deliver test results, track progress, facilitate AI chats, administer, maintain, and enhance the Services.</li>
          <li><strong>Personalize Services:</strong> Tailor insights or content based on your data.</li>
          <li><strong>Communicate With You:</strong> Send service updates, respond to inquiries, inform about features, notify about policy changes.</li>
          <li><strong>Analyze & Develop:</strong> Analyze usage, maintain, improve, customize, measure the Services, and develop new features. This includes <strong>training our AI models (like Pokket AI) solely to improve their understanding and responsiveness related to sports psychology concepts and general conversation for enhancing the Services.</strong></li>
          <li><strong>Security & Fraud Prevention:</strong> Detect and prevent misuse, ensure IT system security.</li>
          <li><strong>Legal Compliance & Rights Protection:</strong> Comply with legal obligations, protect our rights, safety, property, and enforce our terms.</li>
          <li><strong>Other Purposes:</strong> For any other purpose disclosed to you when the information was collected.</li>
        </ul>
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
          <p className="font-bold mb-2">Important Reminder on AI Usage:</p>
          <p>
            While we use your data (including test results and chat interactions) to provide and improve the Services and train our AI, this usage is for informational, educational, and technological development purposes only. <strong>It does not constitute psychological analysis, diagnosis, or treatment by a certified professional, nor does it establish a professional-client relationship.</strong>
          </p>
        </div>
        <p>
          We may aggregate and/or de-identify information. De-identified or aggregated data (which cannot reasonably be associated with you) may be used and disclosed for any purpose, including research and development.
        </p>
      </section>

      {/* When We Disclose Information */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">3. When We Disclose Your Information</h2>
        <p className="mb-4">
          We are committed to your privacy. <strong>We do not sell your personal information to third parties or advertising networks.</strong> We disclose your information only in these limited circumstances:
        </p>
        <ul className="list-disc ml-6 mb-4 space-y-2">
          <li>
            <strong>Affiliates:</strong> To our parent companies, subsidiaries, or other companies under common control, who must adhere to this Policy.
          </li>
          <li>
            <strong>Vendors / Service Providers:</strong> To third parties who perform services on our behalf (e.g., cloud hosting, AI model infrastructure, payment processing, customer support tools, internal analytics, legal services). They are contractually bound to confidentiality and can only use the data for the services they provide to us.
          </li>
          <li>
            <strong>Safety and Protection:</strong> If we believe in good faith it's necessary to protect the rights, safety, or property of PokketCoach, our users, or others; enforce our policies; or respond to emergencies.
          </li>
          <li>
            <strong>Analytics (Internal Use Only):</strong> We may share automatically collected or de-identified data with analytics partners (like Google Analytics) solely to help us understand Service usage and improve performance. <strong>This data is not shared for third-party advertising or sold.</strong>
          </li>
          <li>
            <strong>Legal Requirements:</strong> If required by law or legal process (e.g., subpoena, court order).
          </li>
          <li>
            <strong>Business Transfers:</strong> In connection with a merger, acquisition, financing, or sale of assets, where the receiving entity will be bound by similar privacy obligations.
          </li>
          <li>
            <strong>With Your Consent or Direction:</strong> To other parties if you explicitly consent or direct us (e.g., sharing progress with a coach via a specific feature). We won't share personal chat logs or individual test results publicly without your explicit direction.
          </li>
        </ul>
      </section>

      {/* Online Analytics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">4. Online Analytics (Not Advertising)</h2>
        <p className="mb-4">
          We may use third-party analytics services (like Google Analytics) to understand how users interact with our Services. These services use cookies and similar technologies to collect usage data.
        </p>
        <p className="mb-4">
          The information gathered is used solely by PokketCoach to evaluate and improve the Services' performance and user experience. <strong>We do not use these analytics tools to facilitate third-party targeted advertising, nor do we provide this data to advertising networks.</strong>
        </p>
        <p>
          To prevent Google Analytics from using your information for web analytics, you may install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics Opt-Out Browser Add-on</a>. Mobile device settings may offer similar controls for app analytics tracking.
        </p>
         <p className="font-semibold mt-4">
          Tailored Advertising: PokketCoach does not engage in tailored or interest-based advertising using your personal information collected through the Services. We do not share your personal information with third-party advertising networks for these purposes.
         </p>
      </section>

      {/* Your Choices and Rights */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">5. Your Choices and Rights</h2>
        <p className="mb-4">You have certain choices and rights regarding your information:</p>
        <ul className="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Profile Information:</strong> Review, update, or delete certain account information via settings. You can also deactivate your account.</li>
          <li><strong>Marketing Communications:</strong> Unsubscribe from marketing emails via the link provided. Opt-out of push notifications via device settings. (Note: We may still send essential administrative/transactional emails).</li>
          <li><strong>Cookies & Analytics:</strong> Manage cookies via browser settings and opt-out of Google Analytics (see Section 4).</li>
          <li><strong>Privacy Rights (subject to jurisdiction):</strong> Depending on where you live, you may have rights to:
            <ul className="list-circle ml-6 mt-2">
              <li>Access or obtain a copy of your personal information.</li>
              <li>Correct inaccurate information.</li>
              <li>Request deletion of your information (subject to exceptions).</li>
              <li>Restrict or object to certain processing.</li>
              <li>Data portability.</li>
              <li>Withdraw consent (where applicable).</li>
              <li>Lodge a complaint with a data protection authority.</li>
            </ul>
          </li>
          <li><strong>No Sale or Sharing for Targeted Ads:</strong> As PokketCoach does not sell personal information or share it for targeted advertising, the right to opt-out of such activities is not applicable to our practices.</li>
        </ul>
        <p>
          To exercise your rights, please contact us (see Section 12). We'll verify your identity before processing requests. You have the right not to be discriminated against for exercising your rights. You may be able to designate an authorized agent under applicable law.
        </p>
      </section>

      {/* Regional Disclosures */}
       <section className="mb-8">
         <h2 className="text-2xl font-semibold mb-4">6. Regional Privacy Disclosures</h2>
         <p className="mb-4">Specific disclosures for residents of certain regions:</p>
         <ul className="list-disc ml-6 mb-4 space-y-2">
           <li>
             <strong>EEA/UK Residents:</strong> PokketCoach, Inc. is the data controller. Legal bases include contractual necessity, consent, legitimate interests (including but not limited to operating/improving service, security, legal defense), and legal compliance. You have rights described in Section 5 and the right to complain to a data protection authority.
           </li>
           <li>
             <strong>Nevada Residents:</strong> Nevada law permits residents to opt-out of the "sale" of "covered information." <strong>As PokketCoach does not sell covered information as defined under Nevada law, this opt-out is not applicable.</strong>
           </li>
           <li>
             <strong>California Residents (CCPA):</strong>
             <ul className="list-circle ml-6 mt-2 space-y-1">
                <li><em>Categories Collected/Used:</em> Identifiers, customer records info (payment if applicable), commercial info, internet/network activity, general geolocation, potentially sensitive personal info (account credentials; potentially sensitive data within chats/tests used only for permitted purposes like providing the service, security, service improvement/AI training), inferences. See Sections 1 & 2.</li>
                <li><em>Sources:</em> Directly from you, automatically, third parties. See Section 1.</li>
                <li><em>Disclosure for Business Purposes:</em> To affiliates, vendors, legal authorities, potential acquirers. See Section 3.</li>
                <li><em>“Sale” and “Sharing”:</em> <strong>PokketCoach does not "sell" personal information as defined by the CCPA. PokketCoach also does not "share" personal information for cross-context behavioral advertising.</strong> Therefore, the right to opt-out of sale/sharing is not applicable. We do not knowingly sell or share the personal information of minors under 16.</li>
                <li><em>Use of Sensitive Personal Information:</em> Used only for purposes permitted under CCPA that do not require a right to limit (e.g., providing services, security, account maintenance, service improvement including AI training).</li>
                <li><em>Data Retention:</em> See Section 9.</li>
                <li><em>Your CCPA Rights:</em> Right to know/access, correct, delete (subject to exceptions). See Section 5. Contact us via Section 12. Right to non-discrimination.</li>
                <li><em>Do-Not-Track:</em> We do not currently respond to Do Not Track signals.</li>
             </ul>
           </li>
         </ul>
       </section>

      {/* Children's Privacy */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">7. Children's Privacy</h2>
        <p className="mb-4">
          The Services are not directed to children under 13 (or 16 in EEA/UK). Use by minors under these ages is prohibited without parental consent. We do not knowingly collect personal information from children under these ages without parental consent.
        </p>
        <p>
          If you believe we have inadvertently collected such information, please contact us so we can delete it.
        </p>
      </section>

      {/* Security */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">8. Data Security</h2>
        <p className="mb-4">
          We implement reasonable technical, administrative, and physical safeguards designed to protect your information from loss, misuse, and unauthorized access or disclosure. Measures include encryption, access controls, security assessments, and secure storage.
        </p>
        <p>
          However, no internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security. You use the Services at your own risk.
        </p>
      </section>

      {/* Data Retention */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">9. Data Retention</h2>
        <p className="mb-4">
          We retain personal information for as long as reasonably necessary for the purposes outlined in this Policy (e.g., maintaining your account, providing Services, AI training, legal compliance, dispute resolution) or as required by law.
        </p>
         <p>
          Data may be retained in de-identified or aggregated form for longer periods for research and development. When information is no longer needed, we will delete or anonymize it according to our data retention schedules.
        </p>
      </section>

      {/* International Transfer */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
        <p className="mb-4">
          PokketCoach is based in the United States. Your information will be processed in the U.S. and potentially other countries where our servers or service providers are located. Data protection laws in these countries may differ from your own.
        </p>
        <p>
          By using the Services, you consent to the transfer, processing, and maintenance of your information in the U.S. and other applicable countries. We utilize appropriate safeguards (like Standard Contractual Clauses where required) for international transfers originating from regions like the EEA/UK.
        </p>
      </section>

      {/* Links to Third Parties */}
      <section className="mb-8">
         <h2 className="text-2xl font-semibold mb-4">11. Links to Third-Party Websites</h2>
         <p>
           The Services may contain links to third-party websites or services not operated by us. We are not responsible for their content or privacy practices. We encourage you to review the privacy policies of any third-party sites you visit.
         </p>
      </section>

      {/* Changes to Policy */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">12. Changes to This Privacy Policy</h2>
        <p className="mb-4">
          We may update this Policy from time to time. We will indicate changes by updating the "Last Updated" date at the top. For material changes, we will provide appropriate notice (e.g., via email or in-app notification) as required by law.
        </p>
        <p>
          Your continued use of the Services after changes are posted confirms your acceptance of the revised Policy. Please review this Policy periodically.
        </p>
      </section>

      {/* Contact Us */}
      <section>
        <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
        <p className="mb-4">
          If you have questions about this Privacy Policy or our practices, or wish to exercise your privacy rights, please contact us:
        </p>
        <p className="mb-2">
          Email: <a href="mailto:privacy@pokketcoach.com" className="text-blue-600 hover:underline">privacy@pokketcoach.com</a>
        </p>
        <p>
          Mail: <br />
          2261 Market Street STE 85022 <br />
          San Francisco, CA 94114 <br />
          Attn: Privacy Compliance / Legal
        </p>
      </section>

       <hr className="my-8 border-gray-300" />


    </div>
  );
};

export default PrivacyPage;